import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import Common from '../../../shared/Common';
import { SortUpIcon, SortDownIcon, MinusIcon, PlusIcon } from '../../../shared/icons/Svgs';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { IStartingIncomeCategory } from './NewAccounts';

import './style.css';

export interface IStartingLocationsTableProps {
  incomeCategories: IStartingIncomeCategory[];
  loading: boolean;
  setIncomeCategories: React.Dispatch<React.SetStateAction<IStartingIncomeCategory[]>>;
  setIncomeCategoriesChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

const StartingIncomeCategoriesTable = (props: IStartingLocationsTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const { incomeCategories, setIncomeCategories, setIncomeCategoriesChanged, loading } = props;

  const columns = useMemo<ColumnDef<IStartingIncomeCategory>[]>(
    () => [
      {
        id: 'actionButton',
        enableSorting: false,
        accessorFn: (row) => row.startingIncomeCategoryId,
        header: () => (
          <div
            onClick={() => {
              setIncomeCategoriesChanged(true);
              setIncomeCategories((prevState) => {
                const minIndex = Math.min(0, ...prevState.map((p) => p.startingIncomeCategoryId)) - 1;
                return [...prevState, { startingIncomeCategoryId: minIndex, incomeCategoryName: '' }];
              });
            }}
          >
            <PlusIcon style={{ cursor: 'pointer', width: '30px', height: '30px', fill: 'var(--app-main-color)' }} />
          </div>
        ),
        cell: (info) => (
            <div
              onClick={() => {
                setIncomeCategoriesChanged(true);
                setIncomeCategories((prevState) => {
                  const selectedValue = info.row.original;
                  return prevState.filter(
                    (elem) => elem.startingIncomeCategoryId !== selectedValue.startingIncomeCategoryId
                  );
                });
              }}
            >
              <MinusIcon style={{ cursor: 'pointer', width: '30px', height: '30px', fill: '#dc3545' }} />
            </div>
        )
      },
      {
        id: 'incomeCategoryName',
        enableSorting: true,
        accessorFn: (row) => row.incomeCategoryName,
        header: () => <span>Nazwa</span>,
      },
    ],
    [setIncomeCategories, setIncomeCategoriesChanged]
  );

  const defaultColumn: Partial<ColumnDef<IStartingIncomeCategory>> = {
    cell: function Cell({ getValue, row: { index }, column: { id }, table }) {
      const initialValue = getValue();
      const [value, setValue] = useState(initialValue);

      const onBlur = () => {
        table.options.meta?.updateData?.(index, id, value);
      };

      useEffect(() => {
        setValue(initialValue);
      }, [initialValue]);

      if (id === 'incomeCategoryName') {
        return (
          <Form.Control
            size='sm'
            type='text'
            onChange={(e) => {
              setValue(e.target.value);
              setIncomeCategoriesChanged(true);
            }}
            onBlur={onBlur}
            value={value as string}
          />
        );
      }
    },
  };

  const items = incomeCategories ?? [];

  const table = useReactTable({
    data: items,
    columns,
    defaultColumn,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (rowIndex, columnId, value) => {
        setIncomeCategories((prevState) => {
          prevState[rowIndex] = {
            ...prevState[rowIndex],
            [columnId]: value,
          };

          return [...prevState];
        });
      },
    },
  });

  return (
    <>
      <table id={'edited-new-income-categories-table'} className='table table-sm mt-2' aria-labelledby='tabelLabel'>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        style: header.column.getCanSort()
                          ? {
                              cursor: 'pointer',
                            }
                          : {},
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                        desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading
            ? Common.Ui.getSkeletonTableRows(5, 4, 35)
            : table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
    </>
  );
};

export default StartingIncomeCategoriesTable;
